import { useDispatch, useSelector } from 'hooks/reactRedux';

import { setUserPreference } from 'redux/actionCreators';

import { HOURS_COLUMN_SPECS } from '../columns/columnSpec';
import { COLUMN_VISIBILITY_PREFERENCE_KEY } from '../EmploymentHours.consts';

import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

/** Dojo's collapsed state: the columns that carried no `*DetailsCell` class. */
const buildDefaultModel = (
  availableFields: string[]
): GridColumnVisibilityModel => {
  const defaults = new Set(
    HOURS_COLUMN_SPECS.filter((spec) => spec.defaultVisible).map(
      (spec) => spec.id
    )
  );

  return Object.fromEntries(
    availableFields.map((field) => [field, defaults.has(field)])
  );
};

/**
 * Drops entries for fields the current CLA flags do not produce. Essential: the column set changes
 * per employment and per year, so a preference saved while PLB was visible would otherwise name
 * columns that no longer exist.
 */
const sanitise = (
  stored: unknown,
  availableFields: string[]
): GridColumnVisibilityModel | null => {
  const parsed = parseStored(stored);

  if (!parsed) {
    return null;
  }

  const available = new Set(availableFields);
  const entries = Object.entries(parsed as Record<string, unknown>).filter(
    ([field, visible]) => available.has(field) && typeof visible === 'boolean'
  );

  return Object.fromEntries(entries) as GridColumnVisibilityModel;
};

/** The stored model as-is. `sanitise` narrows it for reading; writing must keep the rest. */
const parseStored = (stored: unknown): GridColumnVisibilityModel | null => {
  const parsed = typeof stored === 'string' ? safeParse(stored) : stored;

  return !parsed || typeof parsed !== 'object' || Array.isArray(parsed)
    ? null
    : (parsed as GridColumnVisibilityModel);
};

const safeParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Column visibility, persisted per user server-side.
 *
 * Reuses the existing preferences plumbing: dispatching SET_PREFERENCE makes the saga POST the value
 * and mirror it into Dojo (`redux/commonSagas.ts:224-241`), and the value is already in
 * `state.preferences` at boot, so reading it costs no request.
 */
export const useHoursColumnVisibility = (availableFields: string[]) => {
  'use memo';

  const dispatch = useDispatch();
  const stored = useSelector(
    (state) => state.preferences?.[COLUMN_VISIBILITY_PREFERENCE_KEY]
  );

  // the stored model only ever names a subset of fields, so it must layer over the defaults rather
  // than replace them — otherwise a field the CLA flags add later renders visible unconditionally
  // (MUI treats a field missing from columnVisibilityModel as visible)
  const columnVisibilityModel = {
    ...buildDefaultModel(availableFields),
    ...(sanitise(stored, availableFields) ?? {})
  };

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    // MUI only reports the columns the current CLA flags produce, so replacing the stored model
    // would drop the choices made under a different flag set (other employment, other year)
    const value = { ...(parseStored(stored) ?? {}), ...model };

    dispatch(
      setUserPreference({ key: COLUMN_VISIBILITY_PREFERENCE_KEY, value })
    );
  };

  return { columnVisibilityModel, onColumnVisibilityModelChange };
};

export const employmentHoursVisibilityInternals = {
  buildDefaultModel,
  parseStored,
  sanitise
};
